import type { ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import type { ControllerTokens } from '@/features/config/pages/Controllers/uiTokens'
import { useTranslation } from '@/hooks/useTranslation'

interface StationDeletionMembershipsProps {
  title: string
  items: ReadonlyArray<{ id: string | number; name: string }>
  icon?: ReactNode
  isDark?: boolean
  tokens?: ControllerTokens
}

export const StationDeletionMemberships = ({
  title,
  items,
  icon,
  isDark = false,
  tokens,
}: StationDeletionMembershipsProps): JSX.Element => {
  const { t } = useTranslation()

  const text = tokens?.text ?? 'text.main'
  const textHi = tokens?.textHi ?? '#ffffff'
  const muted = tokens?.muted ?? 'text.secondary'
  const card = tokens?.card ?? (isDark ? '#0d2a38' : '#ffffff')
  const line = tokens?.line ?? (isDark ? '#223c48' : '#E4EAEA')
  const hover = tokens?.hover ?? (isDark ? '#0a2836' : '#F4F8F7')
  const danger = tokens?.danger ?? '#FF6B6B'

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {icon ? <Box sx={{ display: 'flex', color: muted }}>{icon}</Box> : null}
        <Typography
          component="h3"
          sx={{
            fontSize: '13.5px',
            fontWeight: 600,
            color: textHi,
          }}
        >
          {title}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 20,
            px: '6px',
            borderRadius: '10px',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
            color: muted,
            fontSize: '11.5px',
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {items.length}
        </Box>
      </Box>

      {items.length ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            pt: '4px',
            maxHeight: '140px',
            overflowY: 'auto',
          }}
        >
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                px: '10px',
                py: '6px',
                borderRadius: '8px',
                backgroundColor: card,
                border: `1px solid ${line}`,
                maxWidth: '100%',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: hover,
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.16)',
                },
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: danger,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: text,
                  overflowWrap: 'anywhere',
                }}
              >
                {item.name}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize: '13px',
            color: muted,
            fontStyle: 'italic',
            pt: '2px',
          }}
        >
          {t('stationDeleteNoMemberships')}
        </Typography>
      )}
    </Stack>
  )
}
